<template>
  <div class="unconfirmed-page">

    <!-- Page header -->
    <div class="page-header" >
      <button class="back-btn" onclick="window.history.back()"><i class="ft-arrow-left"></i></button>
      <div class="header-info">
        <span class="page-title">Place New Order</span>
        <span class="page-subtitle"><i class="ft-calendar"></i> {{ date }}</span>
      </div>
    </div>
    <div style="margin-top: 10px !important;"></div>
    <!-- Cart -->
    <div class="section-pad" v-if="cart.length > 0" >
      <Cart show-delete="false" show-quantity="false" show-notes="false" show-checkout="true"></Cart>
    </div>

    <!-- Step 1: Ward / Floor selection -->
    <div class="section-pad"  >
      <div class="step-label">
        <div class="step-badge">1</div>
        <span>Select a ward</span>
      </div>

      <!-- Floor tabs -->
      <div class="floor-tabs" v-if="merchant.use_floors == 'Y' && floors.length > 0">
        <button
          class="floor-tab"
          :class="{ 'floor-tab--active': selectedFloorId === floor.floor_id }"
          v-for="floor in floors"
          :key="floor.floor_id"
          @click="selectFloor(floor)"
        >
          <i class="ft-layers"></i> {{ floor.floor_name }}
        </button>
      </div>

      <!-- Ward grid with scroll arrows -->
      <div class="ward-scroll-wrap" v-if="wards.length > 0">
        <button
          class="scroll-arrow scroll-arrow--left"
          @click="scrollWards(-1)"
          v-show="canScrollLeft"
          aria-label="Scroll left"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>

        <div class="ward-grid" ref="wardGrid" @scroll="updateArrows">
          <button
            class="ward-card"
            :class="{ 'ward-card--active': selectedWardId === w.ward_id }"
            v-for="w in wards"
            :key="w.ward_id"
            @click="selectWard(w)"
          >
            <div class="ward-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <span class="ward-card-name">{{ w.ward_name }}</span>
            <div class="ward-card-check" v-if="selectedWardId === w.ward_id">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          </button>
        </div>

        <button
          class="scroll-arrow scroll-arrow--right"
          @click="scrollWards(1)"
          v-show="canScrollRight"
          aria-label="Scroll right"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>

      <div class="no-wards-hint" v-if="wards.length === 0 && merchant.use_floors == 'Y'">
        <i class="ft-info"></i> Select a floor to see wards.
      </div>
    </div>

    <!-- Loading -->
    <div class="loading-state" v-if="loadingBeds">
      <div class="spinner"></div>
      <span>Loading patients…</span>
    </div>

    <!-- Ward summary + search -->
    <div class="section-pad" v-if="!loadingBeds && beds.length > 0">
      <div class="ward-summary">
        <div class="ward-summary-info">
          <div class="step-label" style="margin-bottom:0">
            <div class="step-badge">2</div>
            <span>Select a patient</span>
          </div>
          <span class="bed-count-badge">{{ filteredBeds.length }} patient{{ filteredBeds.length !== 1 ? 's' : '' }}</span>
        </div>
        <div class="search-wrap">
          <i class="ft-search search-icon"></i>
          <input
            class="search-input"
            type="text"
            v-model="searchQuery"
            placeholder="Search by name or bed…"
          >
          <button class="search-clear" v-if="searchQuery" @click="searchQuery = ''">
            <i class="ft-x"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- No search results -->
    <div class="empty-state" v-if="!loadingBeds && beds.length > 0 && filteredBeds.length === 0">
      <i class="ft-search"></i>
      <p>No patients match <strong>"{{ searchQuery }}"</strong></p>
      <button class="clear-search-btn" @click="searchQuery = ''">Clear search</button>
    </div>

    <!-- Empty ward -->
    <div class="empty-state" v-if="!loadingBeds && beds.length === 0 && ward !== ''">
      <i class="ft-inbox"></i>
      <p>No patients found in this ward.</p>
    </div>

    <!-- Bed cards -->
    <div class="section-pad beds-section" v-if="!loadingBeds && filteredBeds.length > 0">
      <div class="beds-grid">
        <div
          class="patient-block"
          v-for="bed in filteredBeds"
          v-bind:key="bed.bed_id"
        >
          <a href="javascript:void(0)" class="bed-card" v-on:click="saveBed(bed); generateLink(bed)">

            <div class="bed-card-top">
              <div class="patient-avatar">{{ bed.patient_name ? bed.patient_name.charAt(0).toUpperCase() : '?' }}</div>
              <div class="bed-info">
                <div class="bed-number">Bed {{ bed.bed_code }}</div>
                <div class="patient-name">{{ bed.patient_name }}</div>
              </div>
              <button
                class="edit-bed-btn"
                @click.stop="editBed(bed)"
                title="Edit patient"
              >
                <i class="ft-more-vertical"></i>
              </button>
            </div>

            <div class="bed-card-meta">
              <span class="menu-pill"><i class="ft-bookmark"></i> {{ bed.menu_type_name }}</span>
              <span class="allergy-pill" v-if="bed.allergies && bed.allergies != ''">
                <i class="ft-alert-triangle"></i> {{ allergies(bed.allergies) }}
              </span>
            </div>

            <div class="important-info" v-if="bed.important_info != null && bed.important_info != ''">
              <i class="ft-alert-circle"></i> {{ bed.important_info }}
            </div>

            <div class="order-breakdown">
              <div class="breakdown-col">
                <div class="breakdown-day">Today</div>
                <div class="breakdown-dots">
                  <span class="meal-dot dot-b" v-if="bed.order_breakdown.today.breakfast">B</span>
                  <span class="meal-dot dot-l" v-if="bed.order_breakdown.today.lunch">L</span>
                  <span class="meal-dot dot-d" v-if="bed.order_breakdown.today.dinner">D</span>
                  <span class="meal-none" v-if="!bed.order_breakdown.today.breakfast && !bed.order_breakdown.today.lunch && !bed.order_breakdown.today.dinner">—</span>
                </div>
              </div>
              <div class="breakdown-divider"></div>
              <div class="breakdown-col">
                <div class="breakdown-day">Tomorrow</div>
                <div class="breakdown-dots">
                  <span class="meal-dot dot-b" v-if="bed.order_breakdown.tomorrow.breakfast">B</span>
                  <span class="meal-dot dot-l" v-if="bed.order_breakdown.tomorrow.lunch">L</span>
                  <span class="meal-dot dot-d" v-if="bed.order_breakdown.tomorrow.dinner">D</span>
                  <span class="meal-none" v-if="!bed.order_breakdown.tomorrow.breakfast && !bed.order_breakdown.tomorrow.lunch && !bed.order_breakdown.tomorrow.dinner">—</span>
                </div>
              </div>
            </div>

            <div class="place-order-cta">
              <i class="ft-plus-circle"></i> Place Order
            </div>

          </a>
        </div>
      </div>
    </div>

    <!-- Modal: Edit Patient -->
    <div class="modal" tabindex="-1" role="dialog" id="editpateint-modal">
      <div class="modal-dialog" role="document">
        <div class="modal-content app-modal">
          <div class="app-modal-header">
            <span>Edit Patient</span>
            <button type="button" class="modal-close-btn" data-dismiss="modal"><i class="ft-x"></i></button>
          </div>
          <div class="modal-body">
            <p class="modal-hint">Select a patient action:</p>
          </div>
          <div class="app-modal-footer">
            <button type="button" class="modal-btn btn-discharge" v-on:click="discharge()" data-dismiss="modal"><i class="ft-log-out"></i> Discharge</button>
            <button type="button" class="modal-btn btn-transfer" v-on:click="transfer()" data-dismiss="modal"><i class="ft-repeat"></i> Transfer</button>
            <button type="button" class="modal-btn btn-edit" v-on:click="editMenu()" data-dismiss="modal"><i class="ft-edit"></i> Edit</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Discharge -->
    <div class="modal" tabindex="-1" role="dialog" id="discharge-modal">
      <div class="modal-dialog" role="document">
        <div class="modal-content app-modal">
          <div class="app-modal-header app-modal-header--danger">
            <span>Discharge Patient</span>
            <button type="button" class="modal-close-btn" data-dismiss="modal"><i class="ft-x"></i></button>
          </div>
          <div class="modal-body">
            <p class="modal-hint">Are you sure you want to discharge this patient? The bed will become empty and any future orders cancelled.</p>
          </div>
          <div class="app-modal-footer">
            <button type="button" class="modal-btn btn-discharge" v-on:click="dischargeNow()" data-dismiss="modal"><i class="ft-log-out"></i> Confirm Discharge</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Transfer -->
    <div class="modal" tabindex="-1" role="dialog" id="transfer-modal">
      <div class="modal-dialog" role="document">
        <div class="modal-content app-modal">
          <div class="app-modal-header">
            <span>Transfer Patient</span>
            <button type="button" class="modal-close-btn" data-dismiss="modal"><i class="ft-x"></i></button>
          </div>
          <div class="modal-body">
            <div class="modal-field">
              <label class="select-label">New Ward</label>
              <div class="select-wrap">
                <select v-model="printer_type">
                  <option value="">Select a ward</option>
                  <option v-for="ward in wards" v-bind:value="ward.ward_id">{{ ward.ward_name }}</option>
                </select>
                <i class="ft-chevron-down select-arrow"></i>
              </div>
            </div>
            <div class="modal-field">
              <label class="select-label">New Bed</label>
              <div class="select-wrap">
                <select v-model="printer_type">
                  <option value="">Select a bed</option>
                  <option v-for="b in beds" v-bind:value="b.bed_id">{{ b.bed_code }}</option>
                </select>
                <i class="ft-chevron-down select-arrow"></i>
              </div>
            </div>
          </div>
          <div class="app-modal-footer">
            <button type="button" class="modal-btn btn-edit" v-on:click="transferNow()" data-dismiss="modal"><i class="ft-check"></i> Save Transfer</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Edit Menu -->
    <div class="modal" tabindex="-1" role="dialog" id="edit-modal">
      <div class="modal-dialog" role="document">
        <div class="modal-content app-modal">
          <div class="app-modal-header">
            <span>Edit Menu Type</span>
            <button type="button" class="modal-close-btn" data-dismiss="modal"><i class="ft-x"></i></button>
          </div>
          <div class="modal-body">
            <div class="modal-field">
              <label class="select-label">Patient</label>
              <input class="modal-input" type="text" :value="bed.patient_name" readonly>
            </div>
            <div class="modal-field">
              <label class="select-label">Menu</label>
              <div class="select-wrap">
                <select v-model="printer_type">
                  <option value="">Select menu</option>
                  <option v-for="menu in menu_types" v-bind:value="menu.id">{{ menu.name }}</option>
                </select>
                <i class="ft-chevron-down select-arrow"></i>
              </div>
            </div>
          </div>
          <div class="app-modal-footer">
            <button type="button" class="modal-btn btn-edit" v-on:click="saveMenu()" data-dismiss="modal"><i class="ft-check"></i> Save Changes</button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import moment from 'moment'
import swal from 'sweetalert'
import Cart from '@/components/navigation/Cart.vue'

export default {
  name: 'ward',
  components: {
    Cart
  },
  data: function () {
    return {
      merchant: {},
      floors:[],
      date: moment().format('DD MMMM YYYY'),
      time: moment().format('h:mm'),
      wards: this.getWards(),
      beds: [],
      periods: this.getPeriods(),
      period: '',
      cart: window.localStorage.getItem('cart') ? JSON.parse(window.localStorage.getItem('cart')) : [],
      ward:'',
      ward_id:'',
      bed:"",
      menu_types:[],
      printer_type:"",
      searchQuery: '',
      loadingBeds: false,
      selectedWardId: '',
      selectedFloorId: '',
      canScrollLeft: false,
      canScrollRight: false
    }
  },
  computed: {
    filteredBeds: function () {
      var q = this.searchQuery.trim().toLowerCase()
      if (!q) return this.beds
      return this.beds.filter(function (b) {
        return (b.patient_name || '').toLowerCase().includes(q) ||
               (b.bed_code || '').toLowerCase().includes(q)
      })
    }
  },
  mounted: function () {
    this.$store.commit('SET_LAYOUT', 'AppLayout')
    this.$nextTick(this.updateArrows)
    var user = JSON.parse(window.localStorage.getItem('user'))
    var that = this

    that.merchant = user.merchant
    that.floors = that.merchant.floors
    
    console.log('ward',that.ward)
    var cart = window.localStorage.getItem('cart') ? JSON.parse(window.localStorage.getItem('cart')) : []
    if (cart.length > 0) {
      swal({
        title: 'Warning',
        text: 'You have items in your cart that hasn\'t been checked out.',
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Checkout',
            value: 'checkout',
            closeModal: true,
            className: 'btn btn-primary',
            visible: true
          },
          confirm: {
            text: 'DELETE CART',
            value: 'delete',
            closeModal: true,
            className: 'btn btn-danger',
            visible: true
          }
        }
      }).then(function (data) {
        if (data === 'delete') {
          window.localStorage.removeItem('cart')
        } else if (data === 'checkout') {
          that.$router.push('/checkout')
        }
      })
    }

    setInterval(function () {
      that.time = moment().format('h:mm')
    }, 60000)

    // Check if get a ward id from URL
    if (this.$route.query && this.$route.query.ward) {
      this.ward = this.$route.query.ward
      console.log(this.ward)
      that.ward_id = this.ward
      //setTimeout(function () {
        that.getBeds()
     // }, 2000)
    }

    
  },
  updated: function () {
    this.$nextTick(this.updateArrows)
  },
  methods: {
    setJson: function(data){
      return JSON.stringify(data)
    },
    scrollWards: function (dir) {
      var el = this.$refs.wardGrid
      if (!el) return
      el.scrollBy({ left: dir * 240, behavior: 'smooth' })
    },
    updateArrows: function () {
      var el = this.$refs.wardGrid
      if (!el) return
      this.canScrollLeft  = el.scrollLeft > 4
      this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 4
    },
    selectFloor: function (floor) {
      this.selectedFloorId = floor.floor_id
      this.wards = floor.wards
      this.beds = []
      this.selectedWardId = ''
      this.searchQuery = ''
    },
    selectWard: function (w) {
      this.selectedWardId = w.ward_id
      this.searchQuery = ''
      this.beds = []
      window.localStorage.setItem('ward', JSON.stringify(w))
      window.localStorage.setItem('menu_type_id', '')
      window.localStorage.setItem('bed', '')

      if (window.localStorage.getItem('free_issue') === 'Y') {
        this.$router.push('/preorder/' + w.ward_id + '/0')
        return
      }

      this.loadBeds(w.ward_id)
    },
    loadBeds: function (ward_id) {
      var that = this
      that.loadingBeds = true

      $.ajax({
        beforeSend: function (xhr) {
          xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
        },
        url: that.$config.apiUrl + 'medicater/beds/' + ward_id + '?date=' + moment().format('YYYY-MM-DD') + '&ward_id=' + ward_id,
        type: 'get',
        success: function (data) {
          that.beds = data.data || []
          that.loadingBeds = false
        },
        error: function (err) {
          that.loadingBeds = false
          var cached = window.localStorage.getItem(this.url)
          if (cached) {
            try { that.beds = JSON.parse(cached).data || [] } catch (e) { that.beds = [] }
          } else {
            swal({ text: 'Could not load patients. Please check your connection.', icon: 'info', button: 'Ok' })
          }
        }
      })
    },
    getWards: function () {
      var user = JSON.parse(window.localStorage.getItem('user'))
      console.log('use_floors',user.merchant.use_floors)
      if(user.merchant.use_floors == "Y"){
        return []
      }else{
        return user.merchant.wards
      }
      
    },
    floorChanged: function(event){
     this.wards = []
     this.wards = JSON.parse(event.target.value)
     console.log('wards',this.wards)
    },
    getPeriods: function () {
      var user = JSON.parse(window.localStorage.getItem('user'))
      var periods = []

      for (var key in user.merchant.periods) {
        periods.push({
          id: key,
          title: user.merchant.periods[key]
        })
      }

      return periods
    },
    showBed: function (bed) {
      /*
      var show = true
      var that = this

      if (this.period === '') {
        show = false
      } else if (bed.todays_orders) {
        bed.todays_orders.forEach(function (order) {
          if (order.period_id === that.period) {
            show = false
          }
        })
      }

      return show
      */
      return true
    },
    allergies: function (bedAllergies) {
      var allergies = ''

      bedAllergies.forEach(function (allergy) {
        allergies += allergy.name + ', '
      })

      return allergies.substring(0, allergies.length - 2)
    },
    getBeds: function(){
      var that = this
      if(window.localStorage.getItem('free_issue') == "Y"){
        return false
      }
     
      if(that.ward != '' && that.ward != undefined && that.ward != null){
          var ward_id = that.ward
          $.ajax({
              beforeSend: function (xhr) {
              xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
            },
            url: this.$config.apiUrl +'medicater/beds/'+ward_id+'?date=' + moment().format('YYYY-MM-DD'),
            type: 'get',
            success: function (data) {
              //that.beds = data.data

              setTimeout(function () {
                $('.patient-block .card').matchHeight()
              }, 1000)
            },
            error: function (data) {
              console.log('data',data)
              if (window.localStorage.getItem(this.url)) {
                var offlineData = JSON.parse(window.localStorage.getItem(this.url))
                this.success(offlineData)
              } else {
                swal({
                  text: data.responseJSON.message,
                  icon: 'info',
                  button: 'Ok'
                })
                .then(function (data) {
                  that.logout()
                })
              }
            }
        })
      }
    
    },
    wardChanged: function (event) {
      var that = this
      var ward = JSON.parse(event.target.value)
      var ward_id = ward.ward_id
       window.localStorage.setItem('menu_type_id', "")
      window.localStorage.setItem('bed',"")
       window.localStorage.setItem('ward', JSON.stringify(ward))
      if(window.localStorage.getItem('free_issue') == "Y"){
        var ward_new = JSON.parse(window.localStorage.getItem('ward'))
        this.$router.push('/preorder/' +ward_new.ward_id + '/0')
        return false
      }
      if (ward_id !== '') {
        that.loadingBeds = true
        that.beds = []
        that.searchQuery = ''
        $.ajax({
            beforeSend: function (xhr) {
            xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
          },
          url: this.$config.apiUrl +'medicater/beds/'+ward_id+'?date=' + moment().format('YYYY-MM-DD')+'&ward_id='+ward_id,
          type: 'get',
          success: function (data) {
            that.beds = data.data
            that.loadingBeds = false
            setTimeout(function () {
              $('.patient-block .card').matchHeight()
            }, 1000)
          },
          error: function (data) {
            that.loadingBeds = false
            if (window.localStorage.getItem(this.url)) {
              var offlineData = JSON.parse(window.localStorage.getItem(this.url))
              this.success(offlineData)
            } else {
              swal({
                text: data.responseJSON.message,
                icon: 'info',
                button: 'Ok'
              })
              .then(function () { that.logout() })
            }
          }
        })
      }
    },
     logout: function () {
      localStorage.clear()
      this.$router.push('/login')
    },
    generateLink: function (bed) {
      window.localStorage.setItem('menu_type_id', "")
      window.localStorage.setItem('bed',"")

      var that = this
      window.localStorage.setItem('menu_type_id', bed.menu_type_id)
      window.localStorage.setItem('bed',JSON.stringify(bed))
      if(window.localStorage.getItem('bed') != "" && 
        window.localStorage.getItem('bed') != undefined && 
        window.localStorage.getItem('bed') != null &&
        window.localStorage.getItem('menu_type_id') != "" && 
        window.localStorage.getItem('menu_type_id') != undefined && 
        window.localStorage.getItem('menu_type_id') != null){
        that.$router.push('/preorder/' + bed.ward_id + '/' + bed.bed_id)
      }
      
      
    },
    saveBed: function (bed) {
      window.localStorage.setItem('bed', JSON.stringify(bed))
    },
    editBed: function(bed){
      this.bed = bed
      $('#editpateint-modal').modal('show');
    },
    discharge: function(){
      $('#discharge-modal').modal('show');
    },
    transfer: function(){
      $('#transfer-modal').modal('show');
    },
    editMenu: function(){
      $('#edit-modal').modal('show');
    },
    dischargeNow: function(){

    },
    transferNow: function(){

    },
    saveMenu: function(){

    }
  }
}
</script>

<style scoped>
.unconfirmed-page {
  min-height: 100vh;
  background: #f0f4f8;
  padding-bottom: 40px;
}

/* ── Header info ── */
.header-info { display: flex; flex-direction: column; gap: 3px; flex: 1; min-width: 0; }
.page-subtitle {
  font-size: 0.73rem;
  color: rgba(255,255,255,0.65);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}
.page-subtitle i { font-size: 0.65rem; }

/* ── Floor tabs ── */
.floor-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.floor-tab {
  padding: 7px 14px;
  border-radius: 999px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.15s;
}
.floor-tab--active {
  background: #1a587b;
  border-color: #1a587b;
  color: #fff;
}

/* ── Ward scroll wrapper ── */
.ward-scroll-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
}

.scroll-arrow {
  flex-shrink: 0;
  width: 32px; height: 32px;
  border-radius: 50%;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  color: #1a587b;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: background 0.15s, border-color 0.15s, transform 0.1s;
  z-index: 1;
}
.scroll-arrow:hover  { background: #e0f2fe; border-color: #1a587b; }
.scroll-arrow:active { transform: scale(0.93); }

/* ── Ward grid ── */
.ward-grid {
  display: flex;
  flex-direction: row;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 6px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: .5em;
}
.ward-grid::-webkit-scrollbar { display: none; }

.ward-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 16px 12px;
  background: #fff;
  border: 1.5px solid #e8edf2;
  border-radius: 14px;
  box-shadow: 0 -3px 8px rgba(18,69,100,0.18), 0 5px 14px rgba(18,69,100,0.22);
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.1s;
  flex: 0 0 110px;
  min-width: 110px;
}
.ward-card:hover {
  border-color: #1a587b;
  box-shadow: 0 4px 14px rgba(26,88,123,0.12);
}
.ward-card:active { transform: scale(0.97); }

.ward-card--active {
  border-color: #1a587b;
  background: #f0f9ff;
  box-shadow: 0 4px 14px rgba(26,88,123,0.18);
}

.ward-card-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #e0f2fe;
  color: #1a587b;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ward-card--active .ward-card-icon {
  background: #1a587b;
  color: #fff;
}

.ward-card-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
}

.ward-card-check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #1a587b;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-wards-hint {
  font-size: 0.82rem;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 0;
}

/* ── Step label ── */
.step-label {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 10px;
}
.step-badge {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #1a587b;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.step-label span {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.3px;
  color: #64748b;
}

/* ── Loading ── */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 40px 16px;
  color: #94a3b8;
  font-size: 0.875rem;
}
.spinner {
  position: relative;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: #1a587b;
  border-left-color: #1a587b;
  animation: spin 0.9s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite;
}
.spinner::after {
  content: '';
  position: absolute;
  inset: 5px;
  border-radius: 50%;
  border: 2.5px solid transparent;
  border-top-color: rgba(26, 88, 123, 0.35);
  border-left-color: rgba(26, 88, 123, 0.35);
  animation: spin 1.8s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite reverse;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Ward summary + search ── */
.ward-summary {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ward-summary-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.bed-count-badge {
  font-size: 0.7rem;
  font-weight: 700;
  color: #1a587b;
  background: #e0f2fe;
  padding: 3px 10px;
  border-radius: 999px;
}

/* ── Search ── */
.search-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.search-icon {
  position: absolute;
  left: 13px;
  color: #94a3b8;
  font-size: 0.875rem;
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 11px 38px 11px 38px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.875rem;
  color: #0f172a;
  background: #fff;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
}
.search-input:focus {
  border-color: #1a587b;
  box-shadow: 0 0 0 3px rgba(26,88,123,0.1);
}
.search-input::placeholder { color: #b0bcc8; }
.search-clear {
  position: absolute;
  right: 10px;
  width: 22px;
  height: 22px;
  border: none;
  background: #e2e8f0;
  border-radius: 50%;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  cursor: pointer;
}

/* ── Edit button on card ── */
.edit-bed-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: auto;
  transition: background 0.15s;
}
.edit-bed-btn:hover { background: #e0f2fe; color: #1a587b; }

/* ── Place order CTA ── */
.place-order-cta {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  background: #124564;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  border-top: none;
  border-radius: 15px;
  margin-top: auto;
  margin-bottom: 1em;
  max-width: 90%;
  align-self: center;
  width: 100%;
}

/* ── Clear search button ── */
.clear-search-btn {
  padding: 8px 16px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  color: #1a587b;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 4px;
}

/* ── Beds section spacing ── */
.beds-section { padding-top: 12px; }

/* ── Page header ── */
.page-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 30px 20px 20px;
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 10;
  margin-top: 3.2rem;
  border-radius: 0;
  box-shadow: 0 8px 32px rgba(15, 61, 92, 0.35);
}

.back-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}

.back-btn:hover { background: rgba(255,255,255,0.25); }

.page-title {
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.3px;
}

/* ── Sections ── */
.section-pad {
 
}

/* ── Selection card ── */
.selection-card {
  background: #fff;
  border-radius: 14px;
  padding: 18px 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.select-group { display: flex; flex-direction: column; gap: 6px; }

.select-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 5px;
}

.select-wrap {
  position: relative;
}

.select-wrap select {
  width: 100%;
  appearance: none;
  -webkit-appearance: none;
  padding: 11px 40px 11px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #1e293b;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.select-wrap select:focus {
  border-color: #1a8a7a;
  box-shadow: 0 0 0 3px rgba(26,138,122,0.12);
  background: #fff;
}

.select-arrow {
  position: absolute;
  right: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
  font-size: 0.85rem;
}

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: rgba(255,255,255,0.6);
  text-align: center;
  gap: 12px;
}

.empty-state i { font-size: 2.5rem; opacity: 0.5; }
.empty-state p { font-size: 0.95rem; margin: 0; }

/* ── Beds grid ── */
.beds-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
.patient-block { display: contents; }

/* ── Bed card ── */
.bed-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fff;
  border-radius: 14px;
  padding: 14px 14px 0;
  box-shadow: 0 4px 16px rgba(0,0,0,0.14), 0 1px 4px rgba(0,0,0,0.10);
  text-decoration: none !important;
  color: #1e293b !important;
  transition: transform 0.15s, box-shadow 0.15s;
  overflow: hidden;
  height: 100%;
}

.bed-card:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(0,0,0,0.13);
}

.bed-card-top {
  display: flex;
  align-items: center;
  gap: 10px;
}

.patient-avatar {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, #0f4c81, #1a8a7a);
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.bed-info { flex: 1; min-width: 0; }

.bed-number {
  font-size: 0.7rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.patient-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Meta pills ── */
.bed-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.menu-pill, .allergy-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 999px;
}

.menu-pill {
  background: rgba(26,138,122,0.1);
  color: #1a8a7a;
}

.allergy-pill {
  background: rgba(220,38,38,0.08);
  color: #dc2626;
}

.important-info {
  font-size: 0.75rem;
  color: #d97706;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 8px;
  background: rgba(217,119,6,0.08);
  border-radius: 8px;
}

/* ── Meal breakdown ── */
.order-breakdown {
  display: flex;
  margin: 4px -14px 0;
  border-top: 1px solid #999a9b;
  background: #f8fafc;
  border-radius: 0 0 14px 14px;
}

.breakdown-col {
  flex: 1;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.breakdown-divider {
  width: 1px;
  background: #999a9b;
  margin: 8px 0;
}

.breakdown-day {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
}

.breakdown-dots { display: flex; gap: 4px; flex-wrap: wrap; }

.meal-dot {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  font-size: 0.65rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.dot-b { background: #0f4c81; }
.dot-l { background: #1a8a7a; }
.dot-d { background: #7c3aed; }

.meal-none { font-size: 0.8rem; color: #cbd5e1; line-height: 22px; }

/* ── Modals ── */
.app-modal {
  border: none;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
}

.app-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #0f4c81, #1a8a7a);
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
}

.app-modal-header--danger {
  background: linear-gradient(135deg, #991b1b, #dc2626);
}

.modal-close-btn {
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.85rem;
}

.modal-hint {
  color: #64748b;
  font-size: 0.9rem;
  margin: 0;
}

.app-modal-footer {
  display: flex;
  gap: 8px;
  padding: 12px 16px 16px;
  flex-wrap: wrap;
}

.modal-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}

.modal-btn:hover { opacity: 0.88; }

.btn-discharge { background: #fee2e2; color: #dc2626; }
.btn-transfer  { background: #dbeafe; color: #1d4ed8; }
.btn-edit      { background: #d1fae5; color: #065f46; }

.modal-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }

.modal-input {
  padding: 10px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #475569;
  background: #f8fafc;
  outline: none;
  width: 100%;
}
</style>
